import React from 'react';
import { compose } from 'recompose';
import { Redirect } from 'react-router';
import { RouteComponentProps } from 'react-router-dom';
import { Loader } from '@unmind/design-system';
import { Severity } from '@sentry/browser';
import { LoggedOutScreenWrapper } from 'LoggedOut/LoggedOutScreenWrapper/LoggedOutScreenWrapper';
import { logException } from '../../App/logging';
import {
  withSubdomainFromUrl,
  ChildProps as SubdomainFromUrlProps,
} from '../withSubdomainFromUrl';
import RoutePath from '../../App/RoutePath';
import { AuthTypeEnum } from '../../__generated__/globalTypes';
import { useSubdomainInfo } from './useSubdomainInfo';
import { useValidateInvitationToken } from './hooks/useValidateInvitationToken';
import { InvitedUserSignUp } from './InvitedUserSignUp';

export interface SignUpProps
  extends RouteComponentProps, SubdomainFromUrlProps {
  skipRedirectForTest?: boolean;
}

export interface CustomIdentifier {
  fieldKey: string;
  fieldValue: string;
}

export interface UserDetails {
  email?: string;
  customIdentifiers?: CustomIdentifier[];
  firstName?: string;
  lastName?: string;
  location?: string;
  department?: string;
  password?: string;
  explicitPrivacyConsentGiven?: boolean;
  explicitEEADataStorageConsentGiven?: boolean;
  explicitHealthDataConsentGiven?: boolean;
  marketingOptIn?: boolean;
  piplPrivacyPolicyConsentGiven?: boolean;
  piplSensitiveDataConsentGiven?: boolean;
  piplThirdPartySharingConsentGiven?: boolean;
  piplCrossBorderTransferConsentGiven?: boolean;
  isInvitedUser?: boolean;
  invitationToken?: string;
}

export enum FormType {
  CustomIdentifiers,
  CompanyEmail,
  InvitedUser,
}

export const SIGNUP_BASE_PATH = '/signup';

export function SignUp({ subdomain }: SignUpProps) {
  const { hasLovedOneInvitationCode } = useValidateInvitationToken();

  const {
    error: subdomainError,
    loading: loadingSubdomainInfo,
    group,
  } = useSubdomainInfo({
    subdomain,
  });

  const { authType } = group ?? { authType: null };
  const isUsingSSO = authType === AuthTypeEnum.AUTH0_SSO;

  if (subdomainError) {
    logException(subdomainError, {
      level: Severity.Warning,
      tags: {
        subdomain,
      },
    });
  }

  if (loadingSubdomainInfo) {
    return (
      <LoggedOutScreenWrapper data-testid="signup-v2">
        <Loader />
      </LoggedOutScreenWrapper>
    );
  }

  if (!hasLovedOneInvitationCode) {
    return isUsingSSO ? (
      <Redirect to={RoutePath.LoginWithSSO} />
    ) : (
      <Redirect to={RoutePath.AccountVerification} />
    );
  }

  return <InvitedUserSignUp />;
}

export default compose<SignUpProps, RouteComponentProps>(withSubdomainFromUrl)(
  SignUp,
);
